/**
 * Dialog de aguardando resposta do cliente (Lado Agente)
 * Exibe feedback visual enquanto aguarda accept/reject do cliente
 * 
 * @author Sistema
 * @version 1.0.0
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Monitor, Loader2 } from 'lucide-react';

interface ScreenShareWaitingDialogProps {
  /** Se true, exibe o dialog */
  open: boolean;
  /** Nome do contato que receberá a solicitação */
  contactName: string;
  /** Callback para cancelar a solicitação */
  onCancel: () => void;
}

/**
 * Modal que exibe feedback visual para o agente enquanto aguarda
 * o cliente aceitar ou recusar a solicitação de visualização de tela.
 * 
 * Comportamento:
 * - Exibe ícone animado de monitor com pulso
 * - Mostra nome do contato
 * - Permite cancelar a solicitação
 * - Não fecha ao clicar fora (preventCloseOnOutsideInteraction)
 */
export function ScreenShareWaitingDialog({
  open,
  contactName,
  onCancel,
}: ScreenShareWaitingDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent 
        preventCloseOnOutsideInteraction 
        hideCloseButton
        className="max-w-sm"
      >
        <DialogHeader className="text-center">
          {/* Ícone de monitor com animação de pulso */}
          <div className="mx-auto mb-4 relative">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Monitor className="h-8 w-8 text-primary" />
            </div>
            {/* Indicador de pulso animado */}
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-4 w-4 bg-primary" />
            </span>
          </div>
          
          <DialogTitle>Aguardando resposta...</DialogTitle>
          <DialogDescription className="text-center">
            Solicitação enviada para <strong>{contactName}</strong>.
            <br />
            O cliente precisa aceitar para iniciar a visualização.
          </DialogDescription>
        </DialogHeader>
        
        {/* Indicador de loading centralizado */}
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
        
        <DialogFooter className="sm:justify-center">
          <Button variant="outline" onClick={onCancel}>
            Cancelar solicitação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
